import { useMemo } from "react";
import { When } from "react-if";
import { IEmailOpenTimeCell } from "shared";

import {
  BUCKETS,
  DAYS,
  HeatmapGrid,
  HeatmapLegend,
  IHeatmapCell,
} from "./SendTimeHeatmap";
import {
  formatCount,
  formatPct,
  rate,
} from "./stats";

interface IProps {
  openTimes: IEmailOpenTimeCell[];
  /** Zone the cells were bucketed in */
  timezone: string;
}

export function OpenTimeHeatmap({ openTimes, timezone }: IProps) {
  const { rows, top, max, total } = useMemo(() => {
    const total = openTimes.reduce((s, c) => s + c.opened, 0);

    let top: { day: string; bucket: string; share: number } | null = null;

    let max = 0;

    const rows = DAYS.map((day, dayIdx) => {
      const cells = BUCKETS.map(bucket => {
        const opened = openTimes
          .filter(c => c.isoDayOfWeek === dayIdx + 1 && bucket.hours.includes(c.hour))
          .reduce((s, c) => s + c.opened, 0);
        const share = opened > 0 ? rate(opened, total) : null;

        if (share !== null) {
          max = Math.max(max, share);

          if (!top || share > top.share) {
            top = { day, bucket: bucket.label, share };
          }
        }

        return {
          value: share,
          label: share === null ? `–` : `${Math.round(share)}%`,
          title: `${day} ${bucket.label}: ${formatCount(opened)} of ${formatCount(total)} opens`,
        } as IHeatmapCell;
      });

      return { day, cells };
    });

    return { rows, top, max, total };
  }, [openTimes]);

  if (total === 0) {
    return null;
  }

  return (
    <div className={ `flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-5` }>
      <div className={ `flex flex-wrap items-end justify-between gap-3` }>
        <div className={ `flex flex-col gap-0.5` }>
          <h2 className={ `text-base font-semibold text-gray-900` }>{ `Most common open times` }</h2>
          <div className={ `text-[13px] text-gray-500` }>
            { `All templates, ${timezone}. ` }
            <When condition={ !!top }>
              { `Most opens: ` }
              <strong className={ `text-paidniceGreen-700` }>{ `${top?.day} ${top?.bucket} (${formatPct(top?.share ?? null)})` }</strong>
            </When>
          </div>
        </div>
        <HeatmapLegend min={ 0 }
          max={ max } />
      </div>
      <HeatmapGrid rows={ rows }
        min={ 0 }
        max={ max } />
      <div className={ `text-xs text-gray-400` }>
        { `Share of the ${formatCount(total)} emails first opened in this period, by when they were opened. Other = opens between 8pm and 6am.` }
      </div>
    </div>
  );
}
